import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { BaseApiService } from '../../shared/base-service/base-api.service';
import { DriverExaminationSourceType } from '../models/enums/driver-examination-source-type.enum';
import { DriverExaminationResultCSTIMS } from '../models/examination/driver-examination-result-cstims.model';
import { CustomerIndividualVLSAssessment } from '../models/external-checks/customer-individual-vls-assessment.model';
import { CustomerIndividualVLSResponse } from '../models/external-checks/customer-individual-vls-response.model';
import { NationalRegistryRequestData } from '../models/external-checks/nrii-request-data.model';

@Injectable({
    providedIn: 'root'
})
/**
 * actions related to the driver external checks
*/
export class ExternalCheckService extends BaseApiService {

    private driverExaminationSourceType = new DriverExaminationSourceType();

    constructor() {
        super();
    }

    /**
     * Returns the lookups for external checks
     * @param driverExaminationTypeId
     */
    public getLookups() {
        return this.get('Driver/ExternalCheck/Lookups');
    }

    // USPVS

    /**
     * Returns the current uspvs status/detail
     * @param driverExaminationTypeId
     */
    public getUspvsStatus(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/Uspvs/' + businessTransactionNumberId);
    }

    /**
     * Indicates to the api to run uspvs
     * @param driverExaminationTypeId
     */
    public updateUspvsStatus(businessTransactionNumberId: number) {
        return this.post('Driver/ExternalCheck/Uspvs/' + businessTransactionNumberId, {});
    }

    // SSOLV

    /**
     * Returns the current ssolv status/detail
     * @param driverExaminationTypeId
     */
    public getSsolvStatus(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/Ssolv/' + businessTransactionNumberId);
    }

    /**
     * Indicates to the api to run ssolv
     * @param driverExaminationTypeId
     */
    public updateSsolvStatus(businessTransactionNumberId: number) {
        return this.post('Driver/ExternalCheck/Ssolv/' + businessTransactionNumberId, {});
    }

    /**
     * Indicates to the api to skip the external check
     * @param driverExaminationTypeId
     * @param driverRequirementTypeId
     */
    public skipExternalCheck(businessTransactionNumberId: number, driverRequirementTypeId: number) {
        return this.post('Driver/ExternalCheck/Skip/' + driverRequirementTypeId + '/' + businessTransactionNumberId, {});
    }

    // SPEXS

    /**
     * Indicates to the api to get spexs status
     * @param driverExaminationTypeId
     */
    public getSpexsStatus(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/Spexs/' + businessTransactionNumberId);
    }
    /**
     * Indicates to the api to run spexs
     * @param driverExaminationTypeId
     */
    public updateSpexsStatus(businessTransactionNumberId: number, nameId: number) {
        return this.post('Driver/ExternalCheck/Spexs/' + businessTransactionNumberId + '/' + nameId, {});
    }

    public runSearchEnquiry(customerNumberId: number) {
        return this.post('Driver/ExternalCheck/Spexs/' + customerNumberId, {});
    }

    /**
    * Indicates to the api to get spexs results
    * @param driverExaminationTypeId
    */
    public getSpexsResults(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/SpexResponse/' + businessTransactionNumberId);
    }
    /**
    * Indicates to the api to run spexs
    * @param driverExaminationTypeId
    */
    public saveSpexsMatch(content: any) {
        return this.post('Driver/ExternalCheck/SpexResponse', content);
    }

    // PDPS

    /**
     * Indicates to the api to get pdps status
     * @param driverExaminationTypeId
     */
    public getPdpsStatus(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/Pdps/' + businessTransactionNumberId);
    }
    /**
     * Indicates to the api to run pdps
     * @param driverExaminationTypeId
     */
    public updatePdpsStatus(businessTransactionNumberId: number) {
        return this.post('Driver/ExternalCheck/Pdps/' + businessTransactionNumberId, {});
    }
    /**
    * Indicates to the api to get pdps results
    * @param businessTransactionNumberId
    */
    public getPdpsResults(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/PdpsResponse/' + businessTransactionNumberId);
    }

    // DLN SURVEY

    /**
     * Indicates to the api to get dln surveys from the wip object
     * @param driverExaminationTypeId
     */
    public getWipDlnSurvey(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/WipDlnSurvey/' + businessTransactionNumberId);
    }

    public getDlnSurvey(businessTransactionNumberId: number) {
        return this.get('Driver/ExternalCheck/DlnSurvey/' + businessTransactionNumberId);
    }
    /**
     * Indicates to the api to run dln survey
     * @param driverExaminationTypeId
     */
    public updateDlnSurveyStatus(businessTransactionNumberId: number) {
        return this.post('Driver/ExternalCheck/DlnSurvey/' + businessTransactionNumberId, {});
    }

    public saveWipDlnSurvey(model: any) {
        return this.post('Driver/ExternalCheck/DlnSurvey/Save', model, this.nullReplacer);
    }

    // VLS

    /**
     * Indicates to the api to get vls status
     * @param driverExaminationTypeId
     */
    public getVlsStatus(businessTransactionNumberId: number) {
        return this.get(`Driver/ExternalCheck/Vls/${businessTransactionNumberId}`);
    }
    /**
     * Indicates to the api to run vls
     * @param driverExaminationTypeId
     */
    public updateVlsStatus(businessTransactionNumberId: number) {
        return this.post(`Driver/ExternalCheck/Vls/${businessTransactionNumberId}`, {});
    }

    public saveManualVlsAssessment(businessTransactionNumberId: number, assessment: CustomerIndividualVLSAssessment) {
        return this.post(`Driver/ExternalCheck/Vls/Manual/Assessment/${businessTransactionNumberId}`, assessment);
    }

    public saveManualVlsResponse(response: CustomerIndividualVLSResponse) {
        return this.post('Driver/ExternalCheck/Vls/Manual/Response', response);
    }

    public newVlsStatus(businessTransactionId: number, caseTrackingId: string) {
        return this.post(`Driver/ExternalCheck/Vls/New/${businessTransactionId}/${caseTrackingId}`, {});
    }

    /**
     * Indicates to the api to run vls
     * @param businessTransactionNumberId
     */
    public resubmitVlsStatus(businessTransactionNumberId: number) {
        return this.post(`Driver/ExternalCheck/Vls/Resubmit/${businessTransactionNumberId}`, {});
    }
    /**
    * Indicates to the api to get vls results
    * @param businessTransactionNumberId
    */
    public getVlsResults(businessTransactionNumberId: number) {
        return this.get(`Driver/ExternalCheck/VlsResponse/${businessTransactionNumberId}`);
    }

    /**
    * Indicates to the api to run pdps
    * @param content
    */
    public SavePdpsMatch(content: any) {
        return this.post('Driver/ExternalCheck/PdpsResponse', content);
    }

    public SendTestRequest(bussinessTransactionNumberId: number, driverExaminationSourceTypeId: number = this.driverExaminationSourceType.InOffice.id) {
        return this.get(`Driver/AutoTest/Send/${bussinessTransactionNumberId}?driverExaminationSourceTypeId=${driverExaminationSourceTypeId}`);
    }

    public SendTestRequestForExamTypes = (bussinessTransactionNumberId: number, examTypeIds: number[], driverExaminationSourceTypeId: number = this.driverExaminationSourceType.InOffice.id): Observable<any> => this.post(`Driver/AutoTest/Send/${bussinessTransactionNumberId}/ExamTypes?driverExaminationSourceTypeId=${driverExaminationSourceTypeId}`, examTypeIds);

    public downloadPrintableExam = (bussinessTransactionNumberId: number, driverRequirementTypeId: number, driverExaminationLanguageId: number): Observable<any> => this.get(`driver/examination/downloads/${bussinessTransactionNumberId}/${driverRequirementTypeId}?driverExaminationLanguageId=${driverExaminationLanguageId}`);

    public getDriverExaminationLanguages(bussinessTransactionNumberId: number, driverRequirementTypeId: number) {
        return this.get(`driver/examination/DriverExaminationLanguages/${bussinessTransactionNumberId}/${driverRequirementTypeId}`);
    }

    public getAvailableDriverExaminationLanguages(driverExaminationResultId: number) {
        return this.get(`driver/examination/DriverExaminationLanguages/Result/${driverExaminationResultId}`);
    }

    public getIdentifications(customerNumberId: number) {
        return this.get(`Driver/ExternalCheck/Customer/${customerNumberId}/Identifications`);
    }

    // CSTIMS

    /**
     * Indicates to the api to run cstims
     * @param businessTransactionNumberId
     */
    public updateCstimsStatus(applicantDLN: string, businessTransactionNumberId: number) {
        return this.post('Driver/ExternalCheck/Cstims/' + applicantDLN + '/' + businessTransactionNumberId, {});
    }

    public saveCstimsResult(businessTransactionNumberId: number, model: DriverExaminationResultCSTIMS) {
        return this.post(`Driver/ExternalCheck/Cstims/Result/${businessTransactionNumberId}`, model);
    }

    // DACH

    public getDachStatus(businessTransactionNumberId: number) {
        return this.get(`Driver/ExternalCheck/DACH/${businessTransactionNumberId}`);
    }

    public executeDACHRequest(businessTransactionNumberId: number) {
        return this.post(`Driver/ExternalCheck/DACH/${businessTransactionNumberId}`, {});
    }

    // NRII

    public getNriiStatus(businessTransactionNumberId: number) {
        return this.get(`Driver/ExternalCheck/NRII/${businessTransactionNumberId}`);
    }

    public executeNriiRequest(data: NationalRegistryRequestData) {
        return this.post('Driver/ExternalCheck/NRII/Request', data);
    }

    public getNriiRequestData(customerNumberId: number) {
        return this.get(`Driver/ExternalCheck/NRII/Data/${customerNumberId}`);
    }

    private nullReplacer(key, value) {
        if (value == null) {
            return undefined;
        }

        return value;
    }
}
